<template>
  <div class="report-data-board">
    <section class="hero-band">
      <div class="hero-main">
        <div class="hero-copy">
          <h1>项目数据看板</h1>
          <p>围绕项目维度聚合流量、行为与后续模块页面数据，支持趋势分析、环比对比与前端 Excel 导出。</p>
        </div>
      </div>
      <div class="hero-meta">
        <span>项目：{{ currentProject ? `${currentProject.name} (${currentProject.code})` : '-' }}</span>
        <span>时间范围：{{ activeDateText }}</span>
        <span>对比窗口：{{ compareEnabled ? compareDateText : '未开启' }}</span>
        <span>最近刷新：{{ lastRefreshTime || '-' }}</span>
      </div>
    </section>

    <transition name="board-fade" mode="out-in">
      <section v-if="viewMode === 'list'" class="project-browser">
        <div class="panel-header">
          <div>
            <h3>项目列表</h3>
            <p>先看项目整体情况，再点击卡片进入看板分析。卡片里也预留了埋点检测和后续项目导出入口。</p>
          </div>
        </div>
        <div class="project-toolbar">
          <el-input v-model="projectQuery.keyword" clearable placeholder="搜索项目名称 / code" style="width: 320px"
            @keyup.enter="searchProjects" />
          <el-select v-model="projectQuery.pageSize" style="width: 140px" @change="searchProjects">
            <el-option :value="10" label="10 条/页" />
            <el-option :value="20" label="20 条/页" />
            <el-option :value="50" label="50 条/页" />
          </el-select>
          <el-button @click="searchProjects">搜索</el-button>
          <el-button @click="resetProjectQuery">重置</el-button>
          <el-switch v-model="projectQuery.onlyRunning" inline-prompt active-text="仅运行中" inactive-text="全部"
            @change="handleProjectRunningFilterChange" />
          <el-segmented v-model="projectQuery.version" :options="projectVersionOptions" @change="searchProjects" />
          <el-select v-model="projectQuery.sortField" style="width: 160px" @change="searchProjects">
            <el-option label="按创建顺序" value="id" />
            <el-option label="按 PV" value="pv" />
            <el-option label="按 UV" value="uv" />
            <el-option label="按近3天UV" value="minUv3d" />
            <el-option label="按更新时间" value="updateAt" />
          </el-select>
          <el-segmented v-model="projectQuery.sortOrder" :options="sortOrderOptions" @change="searchProjects" />
        </div>
        <div class="project-card-grid" v-loading="projectLoading">
          <article v-for="item in displayProjectOptions" :key="item.id" class="project-card" :class="{
            'is-current': item.id === selectedProjectId,
            'is-switching': item.id === switchingProjectId
          }" @click="selectProject(item)">
            <div class="project-card-head">
              <div class="project-card-title">
                <h4>{{ item.name }}</h4>
                <p>{{ item.code }}</p>
              </div>
              <div class="project-card-tags">
                <el-tag size="small" effect="plain"
                  :type="normalizeVersion(item.version) === 'V3' ? 'warning' : 'primary'">
                  {{ normalizeVersion(item.version) }}
                </el-tag>
                <el-tag size="small" :type="item.running ? 'success' : 'info'">
                  {{ item.running ? '运行中' : '未运行' }}
                </el-tag>
              </div>
            </div>

            <div class="project-card-metrics">
              <div class="project-metric-item">
                <span>PV</span>
                <strong>{{ formatNumber(item.pv) }}</strong>
              </div>
              <div class="project-metric-item">
                <span>UV</span>
                <strong>{{ formatNumber(item.uv) }}</strong>
              </div>
              <div class="project-metric-item">
                <span>近3天最小UV</span>
                <strong>{{ formatNumber(item.minUv3d) }}</strong>
              </div>
            </div>

            <div class="project-card-extra">
              <span>更新时间</span>
              <strong>{{ item.updateAt || '-' }}</strong>
            </div>

            <div class="project-card-actions">
              <el-button type="primary" :loading="switchingProjectId === item.id" @click.stop="selectProject(item)">
                {{ switchingProjectId === item.id ? '进入中' : '进入看板' }}
              </el-button>
              <el-button plain :loading="trackingCheckLoadingId === item.id" @click.stop="openTrackingCheck(item)">
                检测埋点
              </el-button>
              <el-button plain @click.stop="openProjectExport(item)">项目导出</el-button>
            </div>
          </article>
        </div>
        <div v-if="!projectLoading && !displayProjectOptions.length" class="project-card-empty">
          <el-empty description="当前筛选条件下没有项目数据" />
        </div>
        <div class="project-pagination">
          <el-pagination v-model:current-page="projectQuery.page" v-model:page-size="projectQuery.pageSize" background
            layout="total, prev, pager, next" :total="projectTotal" @current-change="searchProjects" />
        </div>
      </section>
    </transition>

    <transition name="board-fade">
      <section v-if="currentProject && viewMode === 'board'" class="sticky-action-bar">
        <div class="sticky-project">
          <el-button plain size="small" @click="backToProjectList">项目列表</el-button>
          <el-tag size="small" type="primary" effect="plain">当前项目</el-tag>
          <strong>{{ currentProject.name }}</strong>
          <span class="sticky-meta-pill">{{ currentProject.code }}</span>
          <span class="sticky-meta-pill">{{ normalizeVersion(currentProject.version) }}</span>
          <span class="sticky-meta-pill">{{ currentProject.running ? '运行中' : '未运行' }}</span>
          <span class="sticky-meta-pill">PV {{ formatNumber(currentProject.pv) }}</span>
          <span class="sticky-meta-pill">UV {{ formatNumber(currentProject.uv) }}</span>
        </div>
        <div class="sticky-inline-filters">
          <div class="sticky-filter-chip">
            <span>对比</span>
            <el-switch v-model="compareEnabled" />
            <small>{{ compareEnabled ? compareDateText : '关闭' }}</small>
          </div>
          <div class="sticky-filter-chip theme-chip">
            <span>主题</span>
            <el-select v-model="activeTheme" size="small" style="width: 112px">
              <el-option v-for="item in themeOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </div>
        </div>
        <div class="sticky-date">
          <el-date-picker v-model="dateRange" type="daterange" unlink-panels value-format="YYYY-MM-DD"
            range-separator="~" start-placeholder="开始日期" end-placeholder="结束日期" :shortcuts="datePickerShortcuts"
            style="width: 360px" />
        </div>
        <div class="sticky-actions">
          <el-button size="small" plain :loading="trackingCheckLoadingId === currentProject.id"
            @click="openTrackingCheck(currentProject)">检测</el-button>
          <el-button size="small" plain @click="openExportDrawer" :disabled="!currentProject">导出</el-button>
          <el-button size="small" type="primary" :loading="loading" @click="refreshAll">刷新</el-button>
        </div>
      </section>
    </transition>

    <section v-if="!currentProject && viewMode === 'board'" class="empty-project-shell">
      <el-empty description="请先从上方项目列表选择一个项目，再查看数据看板" />
    </section>

    <template v-if="currentProject && viewMode === 'board'">
      <section class="kpi-grid" v-loading="trafficLoading">
        <article v-for="item in trafficKpis" :key="item.key" class="kpi-card">
          <div class="kpi-head">
            <span>{{ item.label }}</span>
            <el-tag size="small" effect="plain" :type="item.tagType">{{ item.tag }}</el-tag>
          </div>
          <p class="kpi-value">{{ item.value }}</p>
          <p class="kpi-compare" :class="deltaClass(item.delta)">
            {{ formatDeltaText(item.delta, item.percent) }}
          </p>
          <p class="kpi-sub">{{ item.sub }}</p>
        </article>
      </section>

      <el-tabs v-model="activeTab" class="board-tabs">
        <el-tab-pane label="流量数据" name="traffic" lazy>
          <section class="panel-grid two-up">
            <div class="panel-shell" v-loading="trafficLoading">
              <div class="panel-header">
                <div>
                  <h3>整体趋势</h3>
                  <p>按天查看核心流量与行为转化走势</p>
                </div>
                <el-segmented v-model="trafficTrendMetric" :options="trafficMetricOptions" />
              </div>
              <div ref="trafficTrendChartRef" class="chart-box large-chart"></div>
            </div>

            <div class="panel-shell" v-loading="trafficLoading">
              <div class="panel-header">
                <div>
                  <h3>渠道分布</h3>
                  <p>当前时间范围内各渠道贡献占比</p>
                </div>
                <el-radio-group v-model="trafficChannelMetric" size="small">
                  <el-radio-button label="pv">PV</el-radio-button>
                  <el-radio-button label="uv">UV</el-radio-button>
                </el-radio-group>
              </div>
              <div ref="trafficChannelChartRef" class="chart-box medium-chart"></div>
            </div>
          </section>

          <section class="panel-grid split-grid">
            <div class="panel-shell" v-loading="trafficLoading">
              <div class="panel-header">
                <div>
                  <h3>渠道排行</h3>
                  <p>支持勾选导出渠道，便于做横向比较</p>
                </div>
                <el-button text @click="openExportDrawer">配置导出渠道</el-button>
              </div>
              <el-table :data="trafficChannelRows" stripe height="360">
                <el-table-column type="index" width="56" label="#" />
                <el-table-column prop="channel" label="渠道" min-width="220" />
                <el-table-column prop="pv" label="PV" min-width="110">
                  <template #default="{ row }">{{ formatNumber(row.pv) }}</template>
                </el-table-column>
                <el-table-column prop="uv" label="UV" min-width="110">
                  <template #default="{ row }">{{ formatNumber(row.uv) }}</template>
                </el-table-column>
                <el-table-column label="PV占比" min-width="110">
                  <template #default="{ row }">{{ formatPercent(calcRate(row.pv, trafficCurrent.pv)) }}</template>
                </el-table-column>
                <el-table-column label="UV占比" min-width="110">
                  <template #default="{ row }">{{ formatPercent(calcRate(row.uv, trafficCurrent.uv)) }}</template>
                </el-table-column>
              </el-table>
            </div>

            <div class="panel-shell" v-loading="trafficLoading">
              <div class="panel-header">
                <div>
                  <h3>导出预设</h3>
                  <p>流量与行为数据导出参数可快速切换</p>
                </div>
              </div>
              <div class="export-summary">
                <div class="summary-item">
                  <span>当前 Sheet 数</span>
                  <strong>{{ exportSheets.length || 0 }}</strong>
                </div>
                <div class="summary-item">
                  <span>日期方向</span>
                  <strong>{{ exportForm.orientation === 'date-row' ? '日期做表头' : '日期做列头' }}</strong>
                </div>
                <div class="summary-item">
                  <span>指标范围</span>
                  <strong>{{ metricModeLabel }}</strong>
                </div>
                <div class="summary-item">
                  <span>Excel 风格</span>
                  <strong>{{ exportThemeLabel }}</strong>
                </div>
              </div>
              <el-button type="primary" style="width: 100%" @click="openExportDrawer">打开导出配置</el-button>
            </div>
          </section>

          <section class="panel-shell table-panel" v-loading="trafficLoading">
            <div class="panel-header">
              <div>
                <h3>按天明细</h3>
                <p>展示每天的数值、单日环比和转化率</p>
              </div>
            </div>
            <el-table :data="trafficDailyRows" stripe height="420">
              <el-table-column prop="date" label="日期" min-width="120" fixed />
              <el-table-column prop="pv" label="PV" min-width="110">
                <template #default="{ row }">
                  <div class="metric-cell">
                    <span>{{ formatNumber(row.pv) }}</span>
                    <small :class="deltaClass(row.pvMom)">{{ formatMom(row.pvMom) }}</small>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="uv" label="UV" min-width="110">
                <template #default="{ row }">
                  <div class="metric-cell">
                    <span>{{ formatNumber(row.uv) }}</span>
                    <small :class="deltaClass(row.uvMom)">{{ formatMom(row.uvMom) }}</small>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="clickUser" label="点击人数" min-width="120">
                <template #default="{ row }">
                  <div class="metric-cell">
                    <span>{{ formatNumber(row.clickUser) }}</span>
                    <small :class="deltaClass(row.clickUserMom)">{{ formatMom(row.clickUserMom) }}</small>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="clickCount" label="点击次数" min-width="120">
                <template #default="{ row }">
                  <div class="metric-cell">
                    <span>{{ formatNumber(row.clickCount) }}</span>
                    <small :class="deltaClass(row.clickCountMom)">{{ formatMom(row.clickCountMom) }}</small>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="点击率" min-width="110">
                <template #default="{ row }">{{ formatPercent(row.clickRate) }}</template>
              </el-table-column>
              <el-table-column label="流失率" min-width="110">
                <template #default="{ row }">{{ formatPercent(row.dropRate) }}</template>
              </el-table-column>
              <el-table-column prop="avgStayTime" label="平均停留" min-width="120">
                <template #default="{ row }">{{ formatStayTime(row.avgStayTime) }}</template>
              </el-table-column>
              <el-table-column prop="sevenDayReturnUser" label="7日回访" min-width="110">
                <template #default="{ row }">{{ formatNumber(row.sevenDayReturnUser) }}</template>
              </el-table-column>
            </el-table>
          </section>
        </el-tab-pane>

        <el-tab-pane label="行为数据" name="behavior" lazy>
          <section class="filter-strip">
            <el-select v-model="behaviorFilters.channel" clearable placeholder="全部渠道" style="width: 240px"
              @change="refreshBehavior">
              <el-option label="全部渠道" value="" />
              <el-option v-for="item in channelOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-select v-model="selectedBehaviorName" filterable remote clearable reserve-keyword placeholder="搜索并选择行为"
              style="width: 320px" :loading="behaviorSearchLoading" :remote-method="handleBehaviorKeywordSearch"
              @visible-change="handleBehaviorSelectVisibleChange" @change="handleBehaviorSelectChange"
              @clear="handleBehaviorSelectClear">
              <el-option v-for="item in behaviorSelectOptions" :key="item.value" :label="item.label"
                :value="item.value" />
            </el-select>
            <el-button @click="refreshBehavior">查询行为</el-button>
          </section>

          <section class="panel-grid two-up">
            <div class="panel-shell" v-loading="behaviorDetailLoading || behaviorLoading">
              <div class="panel-header">
                <div>
                  <h3>行为按天趋势</h3>
                  <p>{{ selectedBehaviorName || '点击下方行为行查看明细趋势' }}</p>
                </div>
                <el-segmented v-model="behaviorTrendMetric" :options="trafficMetricOptions.slice(0, 2)" />
              </div>
              <div ref="behaviorTrendChartRef" class="chart-box large-chart"></div>
            </div>

            <div class="panel-shell" v-loading="behaviorDetailLoading || behaviorLoading">
              <div class="panel-header">
                <div>
                  <h3>行为渠道分布</h3>
                  <p>{{ selectedBehaviorName ? '当前选中行为的渠道占比' : '待选择行为' }}</p>
                </div>
              </div>
              <div ref="behaviorChannelChartRef" class="chart-box medium-chart"></div>
            </div>
          </section>

          <section class="behavior-detail-band" v-if="selectedBehaviorSummary" v-loading="behaviorDetailLoading">
            <div class="detail-card">
              <span>行为名称</span>
              <strong>{{ selectedBehaviorSummary.indexName }}</strong>
            </div>
            <div class="detail-card">
              <span>PV</span>
              <strong>{{ formatNumber(selectedBehaviorSummary.pv) }}</strong>
            </div>
            <div class="detail-card">
              <span>UV</span>
              <strong>{{ formatNumber(selectedBehaviorSummary.uv) }}</strong>
            </div>
            <div class="detail-card">
              <span>PV变化</span>
              <strong :class="deltaClass(selectedBehaviorSummary.pvDelta)">{{
                formatDeltaText(selectedBehaviorSummary.pvDelta,
                selectedBehaviorSummary.pvDeltaRate) }}</strong>
            </div>
            <div class="detail-card">
              <span>UV变化</span>
              <strong :class="deltaClass(selectedBehaviorSummary.uvDelta)">{{
                formatDeltaText(selectedBehaviorSummary.uvDelta,
                selectedBehaviorSummary.uvDeltaRate) }}</strong>
            </div>
          </section>

          <section class="panel-shell table-panel" v-loading="behaviorLoading">
            <div class="panel-header">
              <div>
                <h3>行为列表</h3>
                <p>支持多选导出；点击行可查看指定行为的趋势与渠道贡献</p>
              </div>
              <div class="table-actions">
                <el-button text
                  @click="behaviorSelection = behaviorRows.slice(0, 3).map(item => item.indexName)">默认勾选前3项</el-button>
              </div>
            </div>
            <el-table :data="behaviorRows" stripe height="460" row-key="indexName"
              @selection-change="handleBehaviorSelectionChange" @row-click="handleBehaviorRowClick">
              <el-table-column type="selection" width="48" />
              <el-table-column prop="indexName" label="行为名称" min-width="360" show-overflow-tooltip />
              <el-table-column prop="pv" label="PV" min-width="110">
                <template #default="{ row }">{{ formatNumber(row.pv) }}</template>
              </el-table-column>
              <el-table-column prop="uv" label="UV" min-width="110">
                <template #default="{ row }">{{ formatNumber(row.uv) }}</template>
              </el-table-column>
              <el-table-column label="PV占比" min-width="110">
                <template #default="{ row }">{{ formatPercent(row.pvShare) }}</template>
              </el-table-column>
              <el-table-column label="UV占比" min-width="110">
                <template #default="{ row }">{{ formatPercent(row.uvShare) }}</template>
              </el-table-column>
              <el-table-column label="PV对比" min-width="150">
                <template #default="{ row }">
                  <span :class="deltaClass(row.pvDelta)">{{ formatDeltaText(row.pvDelta, row.pvDeltaRate) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="UV对比" min-width="150">
                <template #default="{ row }">
                  <span :class="deltaClass(row.uvDelta)">{{ formatDeltaText(row.uvDelta, row.uvDeltaRate) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="详情" min-width="90" fixed="right">
                <template #default="{ row }">
                  <el-button text type="primary" @click.stop="showBehaviorDetail(row)">查看</el-button>
                </template>
              </el-table-column>
            </el-table>
          </section>
        </el-tab-pane>

        <el-tab-pane label="模块数据" name="module" lazy>
          <section class="placeholder-grid">
            <div class="placeholder-shell">
              <h3>模块数据框架已就位</h3>
              <p>后续接入模块主数据、模块按天明细、模块渠道贡献后，可以直接复用当前的图表、对比和导出模式。</p>
              <ul>
                <li>顶部 KPI 区展示模块访问、点击、转化</li>
                <li>中部图表区展示模块趋势与模块贡献排行</li>
                <li>底部表格区展示模块明细与单日环比</li>
              </ul>
            </div>
          </section>
        </el-tab-pane>

        <el-tab-pane label="页面数据" name="page" lazy>
          <section class="placeholder-grid">
            <div class="placeholder-shell">
              <h3>页面数据框架已就位</h3>
              <p>页面维度建议沿用同一套交互：页面列表、选中页面趋势、页面渠道分布、页面导出预设，后面只需要挂新接口。</p>
              <ul>
                <li>适合做页面漏斗、页面跳失和页面转化链路</li>
                <li>导出结构依然支持日期横向 / 纵向切换</li>
                <li>Excel 文件名会自动带上项目、页面分类和日期区间</li>
              </ul>
            </div>
          </section>
        </el-tab-pane>
      </el-tabs>
    </template>

    <el-drawer v-model="exportDrawerVisible" title="导出配置" size="640px" @closed="resetExportDrawerState">
      <div class="export-drawer">
        <el-form label-position="top">
          <el-form-item label="导出时间范围">
            <el-date-picker v-model="exportDateRange" type="daterange" unlink-panels value-format="YYYY-MM-DD"
              range-separator="~" start-placeholder="开始日期" end-placeholder="结束日期" :shortcuts="datePickerShortcuts"
              style="width: 100%" />
          </el-form-item>

          <el-form-item label="日期方向">
            <el-radio-group v-model="exportForm.orientation">
              <el-radio-button label="date-row">日期做表头</el-radio-button>
              <el-radio-button label="date-column">日期做列头</el-radio-button>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="指标类型">
            <el-radio-group v-model="exportForm.metricMode">
              <el-radio-button label="pv">仅 PV</el-radio-button>
              <el-radio-button label="uv">仅 UV</el-radio-button>
              <el-radio-button label="both">PV + UV</el-radio-button>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="Excel 主题">
            <el-radio-group v-model="exportForm.themeKey">
              <el-radio-button v-for="theme in exportThemes" :key="theme.key" :label="theme.key">
                {{ theme.label }}
              </el-radio-button>
            </el-radio-group>
          </el-form-item>

          <div class="export-sheet-builder">
            <div class="panel-header">
              <div>
                <h3>新增 Sheet</h3>
                <p>可以混合添加流量 sheet 和行为 sheet，最终导出到同一个 Excel 文件。</p>
              </div>
            </div>

            <el-form-item label="Sheet 类型">
              <el-radio-group v-model="exportSheetDraft.category" @change="handleExportDraftCategoryChange">
                <el-radio-button label="traffic">流量数据</el-radio-button>
                <el-radio-button label="behavior">行为数据</el-radio-button>
              </el-radio-group>
            </el-form-item>

            <el-form-item label="Sheet 名称">
              <el-input v-model="exportSheetDraft.sheetName" placeholder="例如：整体流量、商品点击行为" />
            </el-form-item>

            <el-form-item label="导出渠道">
              <div class="export-inline-actions">
                <el-button text @click="selectDraftChannels">全选</el-button>
                <el-button text @click="clearDraftChannels">清空</el-button>
              </div>
              <el-checkbox-group v-model="exportSheetDraft.channels" class="checkbox-grid">
                <el-checkbox label="__overall__">整体</el-checkbox>
                <el-checkbox v-for="item in channelOptions" :key="item.value" :label="item.value">{{ item.label
                  }}</el-checkbox>
              </el-checkbox-group>
            </el-form-item>

            <template v-if="exportSheetDraft.category === 'behavior'">
              <el-form-item label="搜索行为">
                <div class="export-search-row">
                  <el-input v-model="exportSheetDraft.behaviorKeyword" clearable placeholder="输入关键字筛选行为，例如 商品 / 点击"
                    @input="handleExportBehaviorKeywordSearch(exportSheetDraft.behaviorKeyword)"
                    @keyup.enter="runExportBehaviorSearch" @clear="handleExportBehaviorKeywordClear" />
                  <el-button :loading="exportBehaviorSearchLoading" @click="runExportBehaviorSearch">搜索</el-button>
                </div>
              </el-form-item>

              <el-form-item label="搜索结果待选">
                <div class="export-inline-actions">
                  <el-checkbox v-model="exportExcludeOtherSheetBehaviors">排除其他 Sheet 已选</el-checkbox>
                  <el-button text @click="selectAllDraftBehaviors">全选当前结果</el-button>
                  <el-button text @click="clearDraftBehaviors">清空当前选择</el-button>
                </div>
                <el-checkbox-group v-model="exportSheetDraft.behaviors" class="checkbox-grid behavior-export-grid">
                  <el-checkbox v-for="item in exportBehaviorOptions" :key="item.value" :label="item.value">
                    {{ item.label }}
                  </el-checkbox>
                </el-checkbox-group>
                <div v-if="!exportBehaviorOptions.length" class="export-empty-tip">先输入关键字搜索，再从结果里勾选行为。</div>
              </el-form-item>

              <el-form-item label="当前 Sheet 已选行为">
                <div class="sheet-selected-preview">
                  <el-tag v-for="item in exportSheetDraft.behaviors" :key="item" size="small" closable
                    @close="removeDraftBehavior(item)">
                    {{ item }}
                  </el-tag>
                  <span v-if="!exportSheetDraft.behaviors.length" class="export-empty-tip">还没有选择行为</span>
                </div>
              </el-form-item>
            </template>

            <el-form-item label="当前 Sheet 已选渠道">
              <div class="sheet-selected-preview">
                <el-tag v-for="item in exportSheetDraft.channels" :key="item" size="small" closable
                  @close="removeDraftChannel(item)">
                  {{ item === '__overall__' ? '整体' : item }}
                </el-tag>
                <span v-if="!exportSheetDraft.channels.length" class="export-empty-tip">还没有选择渠道</span>
              </div>
            </el-form-item>

            <div class="draft-sheet-actions">
              <el-button type="primary" style="width: 100%" @click="appendExportSheet">加入导出清单</el-button>
            </div>
          </div>

          <div class="export-sheet-list">
            <div class="panel-header">
              <div>
                <h3>导出清单</h3>
                <p>你可以继续搜索并新增下一个 sheet，最终一次性导出。</p>
              </div>
            </div>
            <div v-if="exportSheets.length" class="sheet-card-list">
              <article v-for="item in exportSheets" :key="item.id" class="sheet-config-card">
                <div class="sheet-config-head">
                  <div>
                    <strong>{{ item.sheetName }}</strong>
                    <p>{{ item.category === 'traffic' ? '流量数据' : '行为数据' }}</p>
                  </div>
                  <el-button text type="danger" @click="removeExportSheet(item.id)">删除</el-button>
                </div>
                <div class="sheet-config-meta">
                  <span>渠道 {{ item.channels.length }}</span>
                  <span v-if="item.category === 'behavior'">行为 {{ item.behaviors.length }}</span>
                </div>
                <p class="sheet-config-text">
                  {{ item.category === 'traffic'
                    ? `渠道：${formatExportTargets(item.channels)}`
                    : `渠道：${formatExportTargets(item.channels)}；行为：${formatBehaviorPreview(item.behaviors)}` }}
                </p>
              </article>
            </div>
            <el-empty v-else description="还没有加入任何导出 sheet" />
          </div>

          <div class="export-preview">
            <strong>文件预览</strong>
            <p>{{ exportFileNamePreview }}</p>
            <p>{{ exportSummaryText }}</p>
            <p>日期范围：{{ exportDateText }}</p>
          </div>
        </el-form>

        <div class="drawer-footer">
          <el-button @click="exportDrawerVisible = false">取消</el-button>
          <el-button type="primary" :loading="exporting" @click="handleExport">开始导出</el-button>
        </div>
      </div>
    </el-drawer>

    <el-dialog v-model="exportProgressVisible" title="导出进度" width="420px" :close-on-click-modal="false"
      :show-close="false" align-center>
      <div class="export-progress-panel">
        <div class="export-progress-title">{{ exportProgress.message || '准备导出...' }}</div>
        <el-progress :percentage="exportProgress.percentage" :stroke-width="18" :status="exportProgress.status" />
        <div class="export-progress-meta">
          <span>进度 {{ exportProgress.current }} / {{ exportProgress.total }}</span>
          <span>{{ exportProgress.percentage }}%</span>
        </div>
        <div class="export-progress-extra">
          <span>当前对象：{{ exportProgress.currentLabel || '-' }}</span>
          <span>剩余：{{ exportProgress.remaining }}</span>
        </div>
      </div>
      <template #footer>
        <el-button v-if="exportProgress.status === 'exception'" @click="retryLastExport">
          重试
        </el-button>
        <el-button v-if="exportProgress.status === 'success' || exportProgress.status === 'exception'"
          @click="closeExportProgressDialog">
          关闭
        </el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="trackingCheckVisible" title="埋点数据检测" width="920px" destroy-on-close>
      <div class="tracking-check-shell">
        <div class="tracking-check-head">
          <div>
            <strong>{{ trackingCheckProject?.name || '-' }}</strong>
            <p>{{ trackingCheckProject?.code || '-' }} · {{ normalizeVersion(trackingCheckProject?.version) }} · 检查日期 {{
              trackingCheckDate }}</p>
          </div>
          <div class="tracking-check-actions">
            <el-button :loading="trackingChecking" @click="rerunTrackingCheck">重新检查</el-button>
          </div>
        </div>

        <div class="tracking-progress-panel">
          <div class="tracking-progress-title">{{ trackingCheckProgress.message || '准备检查...' }}</div>
          <el-progress :percentage="trackingCheckProgress.percentage" :stroke-width="18" />
        </div>

        <div class="tracking-summary-grid" v-if="trackingCheckSummary.length">
          <div v-for="item in trackingCheckSummary" :key="item.key" class="tracking-summary-card">
            <div class="tracking-summary-top">
              <strong>{{ item.label }}</strong>
              <el-tag size="small" :type="item.statusType">{{ item.statusText }}</el-tag>
            </div>
            <div class="tracking-summary-values">
              <span>PV {{ formatNumber(item.pv) }}</span>
              <span>UV {{ formatNumber(item.uv) }}</span>
              <span v-if="item.hasValue">Value {{ formatNumber(item.value) }}</span>
            </div>
            <p>{{ item.tip }}</p>
          </div>
        </div>

        <div class="tracking-suggestion-shell" v-if="trackingSuggestions.length">
          <h4>检查建议</h4>
          <ul>
            <li v-for="item in trackingSuggestions" :key="item">{{ item }}</li>
          </ul>
        </div>

        <div class="tracking-other-shell" v-if="trackingOtherRows.length">
          <div class="panel-header">
            <div>
              <h3>其他埋点数据</h3>
              <p>未被当前规则归类的数据，先完整展示，方便你继续扩展规则。</p>
            </div>
          </div>
          <el-table :data="trackingOtherRows" stripe max-height="260">
            <el-table-column prop="type" label="type" min-width="120" />
            <el-table-column prop="customtype" label="customtype" min-width="160" show-overflow-tooltip />
            <el-table-column prop="page" label="page" min-width="140" show-overflow-tooltip />
            <el-table-column prop="module" label="module" min-width="140" show-overflow-tooltip />
            <el-table-column prop="pv" label="PV" min-width="90">
              <template #default="{ row }">{{ formatNumber(row.pv) }}</template>
            </el-table-column>
            <el-table-column prop="uv" label="UV" min-width="90">
              <template #default="{ row }">{{ formatNumber(row.uv) }}</template>
            </el-table-column>
            <el-table-column prop="value" label="Value" min-width="100">
              <template #default="{ row }">{{ formatNumber(row.value) }}</template>
            </el-table-column>
          </el-table>
        </div>

        <div class="tracking-other-shell">
          <div class="panel-header">
            <div>
              <h3>原始聚合结果</h3>
              <p>当前是按 type、customtype、page、module 维度聚合后的今日数据。</p>
            </div>
          </div>
          <el-table :data="trackingCheckRows" stripe max-height="320" v-loading="trackingChecking">
            <el-table-column prop="type" label="type" min-width="120" />
            <el-table-column prop="customtype" label="customtype" min-width="180" show-overflow-tooltip />
            <el-table-column prop="page" label="page" min-width="160" show-overflow-tooltip />
            <el-table-column prop="module" label="module" min-width="160" show-overflow-tooltip />
            <el-table-column prop="pv" label="PV" min-width="90">
              <template #default="{ row }">{{ formatNumber(row.pv) }}</template>
            </el-table-column>
            <el-table-column prop="uv" label="UV" min-width="90">
              <template #default="{ row }">{{ formatNumber(row.uv) }}</template>
            </el-table-column>
            <el-table-column prop="value" label="Value" min-width="100">
              <template #default="{ row }">{{ formatNumber(row.value) }}</template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </el-dialog>

    <ProjectExportDialog
      v-model="projectExportVisible"
      :project="projectExportProject"
      :default-range="dateRange"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import * as echarts from 'echarts'
import api from '@/utils/http'
import { buildExportFileName, buildPivotSheet, EXPORT_THEMES, exportWorkbook, getThemeConfig } from './reportDataExport'
import { fetchBasicMain, fetchBehaviorDetail, fetchBehaviorList, fetchChannelList, fetchProjectList } from './reportDataApi'
import ProjectExportDialog from './projectExport/ProjectExportDialog.vue'

const activeTab = ref('traffic')
const activeTheme = ref('aurora')
const themeOptions = [
  { label: 'Aurora', value: 'aurora' },
  { label: 'Slate', value: 'slate' },
  { label: 'Graphite', value: 'graphite' }
]
const sortOrderOptions = [
  { label: '降序', value: 'desc' },
  { label: '升序', value: 'asc' }
]
const projectVersionOptions = [
  { label: '全部版本', value: 'all' },
  { label: 'V4', value: 'v4' },
  { label: 'V3', value: 'v3' }
]
const EXPORT_PREFS_KEY = 'report-data-board-export-prefs'
const datePresetOptions = [
  { label: '昨天', value: 'yesterday' },
  { label: '近3天', value: 'last3' },
  { label: '近7天', value: 'last7' },
  { label: '近1个月', value: 'last30' },
  { label: '近3个月', value: 'last90' },
  { label: '今天', value: 'today' }
]
const datePickerShortcuts = datePresetOptions.map((item) => ({
  text: item.label,
  value: () => {
    const [start, end] = getPresetRange(item.value)
    return [new Date(start), new Date(end)]
  }
}))

const dateRange = ref(getDefaultRange())
const compareEnabled = ref(true)
const selectedProjectId = ref(null)
const projectOptions = ref([])
const projectLoading = ref(false)
const projectTotal = ref(0)
const lastRefreshTime = ref('')
const switchingProjectId = ref(null)

const projectQuery = reactive({
  keyword: '',
  page: 1,
  pageSize: 10,
  onlyRunning: false,
  version: 'all',
  sortField: 'id',
  sortOrder: 'desc'
})

const loading = ref(false)
const trafficLoading = ref(false)
const behaviorLoading = ref(false)
const behaviorDetailLoading = ref(false)
const behaviorSearchLoading = ref(false)
const exporting = ref(false)
const exportProgressVisible = ref(false)
const exportProgress = reactive({
  total: 0,
  current: 0,
  percentage: 0,
  message: '',
  status: '',
  currentLabel: '',
  remaining: 0
})
let exportAutoCloseTimer = null
const lastExportAction = ref(null)

const currentProject = ref(null)
const viewMode = ref('list')
const channelList = ref([])
const trafficCurrent = ref(createEmptyTraffic())
const trafficCompare = ref(createEmptyTraffic())
const behaviorCurrent = ref([])
const behaviorCompare = ref([])
const behaviorDetail = ref(createEmptyBehaviorDetail())
const behaviorCompareDetail = ref(createEmptyBehaviorDetail())

const trafficTrendMetric = ref('pv')
const trafficChannelMetric = ref('uv')
const behaviorTrendMetric = ref('uv')

const behaviorFilters = reactive({
  channel: '',
  keyword: ''
})

const selectedBehaviorName = ref('')
const selectedBehaviorSummary = ref(null)
const behaviorSelection = ref([])

const exportDrawerVisible = ref(false)
const exportThemes = EXPORT_THEMES
const exportForm = reactive({
  orientation: 'date-row',
  metricMode: 'both',
  themeKey: exportThemes[0].key
})
const exportDateRange = ref(getDefaultRange())
const exportSheetDraft = reactive({
  category: 'traffic',
  sheetName: '',
  channels: ['__overall__'],
  behaviorKeyword: '',
  behaviors: []
})
const exportSheets = ref([])
const exportBehaviorSearchLoading = ref(false)
const exportBehaviorPool = ref([])
const exportExcludeOtherSheetBehaviors = ref(true)
const trackingCheckVisible = ref(false)
const projectExportVisible = ref(false)
const projectExportProject = ref(null)
const trackingCheckProject = ref(null)
const trackingCheckRows = ref([])
const trackingCheckSummary = ref([])
const trackingOtherRows = ref([])
const trackingSuggestions = ref([])
const trackingChecking = ref(false)
const trackingCheckLoadingId = ref(null)
const trackingCheckDate = ref(formatDate(new Date()))
const trackingCheckProgress = reactive({
  percentage: 0,
  message: ''
})

const trafficTrendChartRef = ref(null)
const trafficChannelChartRef = ref(null)
const behaviorTrendChartRef = ref(null)
const behaviorChannelChartRef = ref(null)

let trafficTrendChart = null
let trafficChannelChart = null
let behaviorTrendChart = null
let behaviorChannelChart = null
let dashboardRequestSeq = 0
let behaviorSearchTimer = null
let exportBehaviorSearchTimer = null
const TRACKING_CHECK_RULES = [
  {
    key: 'traffic',
    label: '流量',
    hasValue: false,
    match(version, row) {
      return version === 'v3' ? row.type === 'flowSource' : row.type === 'visit'
    }
  },
  {
    key: 'click',
    label: '点击',
    hasValue: false,
    match(version, row) {
      return version === 'v3' ? Boolean(String(row.customtype || '').trim()) : row.type === 'click'
    }
  },
  {
    key: 'stayTime',
    label: '停留时长',
    hasValue: true,
    match(_version, row) {
      return row.type === 'timeOnSite' && toNumber(row.value) > 0
    }
  }
]

const trafficMetricOptions = [
  { label: 'PV', value: 'pv' },
  { label: 'UV', value: 'uv' },
  { label: '点击人数', value: 'clickUser' },
  { label: '点击次数', value: 'clickCount' }
]

const activeDateText = computed(() => `${dateRange.value?.[0] || '-'} ~ ${dateRange.value?.[1] || '-'}`)
const exportDateText = computed(() => `${exportDateRange.value?.[0] || '-'} ~ ${exportDateRange.value?.[1] || '-'}`)
const compareDateText = computed(() => {
  const range = getCompareRange(dateRange.value)
  return `${range[0]} ~ ${range[1]}`
})

const channelOptions = computed(() => {
  return (channelList.value || []).map((item) => ({
    label: item,
    value: item
  }))
})

const displayProjectOptions = computed(() => {
  let list = !projectQuery.onlyRunning
    ? [...projectOptions.value]
    : projectOptions.value.filter((item) => item.running)

  if (projectQuery.version !== 'all') {
    list = list.filter((item) => normalizeVersion(item.version).toLowerCase() === projectQuery.version)
  }

  if (!selectedProjectId.value) return list
  return list.sort((a, b) => {
    if (a.id === selectedProjectId.value) return -1
    if (b.id === selectedProjectId.value) return 1
    return 0
  })
})

const trafficKpis = computed(() => {
  const current = trafficCurrent.value || createEmptyTraffic()
  const compare = trafficCompare.value || createEmptyTraffic()
  return [
    buildKpi('pv', '访问 PV', current.pv, compare.pv, '整体访问量', 'primary'),
    buildKpi('uv', '访问 UV', current.uv, compare.uv, '去重访客量', 'success'),
    buildKpi('clickUser', '点击人数', current.clickUser, compare.clickUser, '有行为的用户数', 'warning'),
    buildKpi('clickCount', '点击次数', current.clickCount, compare.clickCount, '总交互次数', 'danger'),
    buildKpi('clickRate', '点击率', Number(current.clickRate || 0), Number(compare.clickRate || 0), '点击人数 / UV', 'info', true),
    buildKpi('dropRate', '流失率', Number(current.dropRate || 0), Number(compare.dropRate || 0), '1 - 点击率', 'info', true)
  ]
})

const trafficChannelRows = computed(() => {
  return [...(trafficCurrent.value?.channelRateList || [])].sort((a, b) => (b[trafficChannelMetric.value] || 0) - (a[trafficChannelMetric.value] || 0))
})

const trafficDailyRows = computed(() => {
  const rows = trafficCurrent.value?.dailyBasics || []
  return rows.map((item, index) => {
    const prev = index > 0 ? rows[index - 1] : null
    return {
      ...item,
      pvMom: calcMom(item.pv, prev?.pv),
      uvMom: calcMom(item.uv, prev?.uv),
      clickUserMom: calcMom(item.clickUser, prev?.clickUser),
      clickCountMom: calcMom(item.clickCount, prev?.clickCount)
    }
  })
})

const behaviorRows = computed(() => {
  const compareMap = new Map((behaviorCompare.value || []).map((item) => [item.indexName, item]))
  const totalPv = (behaviorCurrent.value || []).reduce((sum, item) => sum + toNumber(item.pv), 0)
  const totalUv = (behaviorCurrent.value || []).reduce((sum, item) => sum + toNumber(item.uv), 0)

  return (behaviorCurrent.value || []).map((item) => {
    const compare = compareMap.get(item.indexName) || {}
    const pv = toNumber(item.pv)
    const uv = toNumber(item.uv)
    const comparePv = toNumber(compare.pv)
    const compareUv = toNumber(compare.uv)
    return {
      ...item,
      pvShare: calcRate(pv, totalPv),
      uvShare: calcRate(uv, totalUv),
      pvDelta: pv - comparePv,
      uvDelta: uv - compareUv,
      pvDeltaRate: calcMom(pv, comparePv),
      uvDeltaRate: calcMom(uv, compareUv)
    }
  }).sort((a, b) => b.pv - a.pv)
})

const behaviorSelectOptions = computed(() => {
  return behaviorRows.value.map((item) => ({
    label: item.indexName,
    value: item.indexName
  }))
})

const exportBehaviorOptions = computed(() => {
  const selectedInOtherSheets = new Set(
    exportSheets.value
      .filter((item) => item.category === 'behavior')
      .flatMap((item) => item.behaviors || [])
  )

  return exportBehaviorPool.value
    .filter((item) => {
      if (!exportExcludeOtherSheetBehaviors.value) return true
      return !selectedInOtherSheets.has(item.indexName) || exportSheetDraft.behaviors.includes(item.indexName)
    })
    .map((item) => ({
      label: item.indexName,
      value: item.indexName,
      pv: item.pv,
      uv: item.uv
    }))
})

const exportFileNamePreview = computed(() => {
  return buildExportFileName({
    projectName: currentProject.value?.name,
    projectCode: currentProject.value?.code,
    dataLabel: exportSheets.value.length > 1 ? '组合导出' : exportSheets.value[0]?.category === 'behavior' ? '行为导出' : '流量导出',
    startDate: exportDateRange.value?.[0] || 'start',
    endDate: exportDateRange.value?.[1] || 'end'
  })
})

const exportSummaryText = computed(() => {
  const trafficCount = exportSheets.value.filter((item) => item.category === 'traffic').length
  const behaviorCount = exportSheets.value.filter((item) => item.category === 'behavior').length
  return `当前已配置 ${exportSheets.value.length} 个 sheet，其中流量 ${trafficCount} 个、行为 ${behaviorCount} 个，指标模式为 ${metricModeLabel.value}。`
})

const metricModeLabel = computed(() => {
  return exportForm.metricMode === 'both' ? 'PV + UV' : exportForm.metricMode.toUpperCase()
})

const exportThemeLabel = computed(() => {
  return getThemeConfig(exportForm.themeKey).label
})

watch(activeTab, async (value) => {
  if (value === 'behavior' && currentProject.value) {
    await loadBehaviorData()
  }
  await nextTick()
  renderCharts()
})

watch([dateRange, compareEnabled], async () => {
  if (!currentProject.value) return
  await refreshAll()
})

watch(exportDateRange, () => {
  exportBehaviorPool.value = []
})

watch(activeTheme, async () => {
  await nextTick()
  renderCharts()
})

watch(
  () => ({
    orientation: exportForm.orientation,
    metricMode: exportForm.metricMode,
    themeKey: exportForm.themeKey
  }),
  (value) => {
    localStorage.setItem(EXPORT_PREFS_KEY, JSON.stringify(value))
  },
  { deep: true }
)

watch(
  () => [trafficTrendMetric.value, trafficChannelMetric.value, behaviorTrendMetric.value],
  async () => {
    await nextTick()
    renderCharts()
  }
)

watch(
  () => [trafficCurrent.value, behaviorDetail.value, selectedBehaviorName.value],
  async () => {
    await nextTick()
    renderCharts()
  },
  { deep: true }
)

onMounted(async () => {
  restoreExportPreferences()
  await searchProjects()
  window.addEventListener('resize', resizeCharts)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeCharts)
  clearBehaviorSearchTimer()
  clearExportBehaviorSearchTimer()
  clearExportAutoCloseTimer()
  disposeCharts()
})

async function loadProjectOptions(query) {
  projectLoading.value = true
  try {
    if (query !== projectQuery.keyword) {
      projectQuery.keyword = query || ''
      projectQuery.page = 1
    }
    const result = await fetchProjectList({
      name: query ?? projectQuery.keyword,
      page: projectQuery.page,
      size: projectQuery.pageSize,
      sortField: projectQuery.sortField,
      sortOrder: projectQuery.sortOrder
    })
    projectOptions.value = result?.list || []
    projectTotal.value = result?.total || 0
    if (selectedProjectId.value) {
      const matched = projectOptions.value.find((item) => item.id === selectedProjectId.value)
      if (matched) currentProject.value = matched
    }
  } catch (error) {
    console.error(error)
  } finally {
    projectLoading.value = false
  }
}

async function searchProjects() {
  await loadProjectOptions(projectQuery.keyword)
}

async function resetProjectQuery() {
  projectQuery.keyword = ''
  projectQuery.page = 1
  projectQuery.pageSize = 10
  projectQuery.onlyRunning = false
  projectQuery.version = 'all'
  projectQuery.sortField = 'id'
  projectQuery.sortOrder = 'desc'
  await searchProjects()
}

async function handleProjectRunningFilterChange() {
  if (selectedProjectId.value && !displayProjectOptions.value.find((item) => item.id === selectedProjectId.value)) {
    // keep current project in dashboard, only list filtered
  }
}

async function selectProject(project) {
  if (!project?.id) return
  if (switchingProjectId.value === project.id || loading.value) return
  switchingProjectId.value = project.id
  selectedProjectId.value = project.id
  currentProject.value = project
  viewMode.value = 'board'
  activeTab.value = 'traffic'
  try {
    await refreshAll()
    await nextTick()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } finally {
    if (switchingProjectId.value === project.id) {
      switchingProjectId.value = null
    }
  }
}

function restoreExportPreferences() {
  try {
    const raw = localStorage.getItem(EXPORT_PREFS_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (parsed?.orientation) exportForm.orientation = parsed.orientation
    if (parsed?.metricMode) exportForm.metricMode = parsed.metricMode
    if (parsed?.themeKey) exportForm.themeKey = parsed.themeKey
  } catch (error) {
    console.error('restore export prefs failed', error)
  }
}

async function refreshAll() {
  if (!currentProject.value?.id) return
  const requestId = ++dashboardRequestSeq
  const projectId = currentProject.value.id
  loading.value = true
  try {
    await Promise.all([
      loadTrafficData({ requestId, projectId }),
      activeTab.value === 'behavior' ? loadBehaviorData({ requestId, projectId }) : Promise.resolve()
    ])
    if (requestId !== dashboardRequestSeq || currentProject.value?.id !== projectId) return
    lastRefreshTime.value = formatNow()
  } finally {
    if (requestId === dashboardRequestSeq) {
      loading.value = false
    }
  }
}

async function loadTrafficData(context = {}) {
  trafficLoading.value = true
  try {
    const currentParams = buildBaseParams()
    const compareParams = buildBaseParams(getCompareRange(dateRange.value))

    const tasks = [
      fetchBasicMain(currentParams),
      fetchChannelList(currentParams)
    ]
    if (compareEnabled.value) {
      tasks.push(fetchBasicMain(compareParams))
    }

    const [main, channels, compareMain] = await Promise.all(tasks)
    if (context.requestId && (context.requestId !== dashboardRequestSeq || currentProject.value?.id !== context.projectId)) {
      return
    }
    trafficCurrent.value = normalizeTraffic(main)
    channelList.value = Array.isArray(channels) ? channels : []
    trafficCompare.value = compareEnabled.value ? normalizeTraffic(compareMain) : createEmptyTraffic()
  } catch (error) {
    console.error(error)
    ElMessage.error(error.message || '加载流量数据失败')
  } finally {
    if (!context.requestId || context.requestId === dashboardRequestSeq) {
      trafficLoading.value = false
    }
  }
}

async function loadBehaviorData(context = {}) {
  if (!currentProject.value?.id) return
  behaviorLoading.value = true
  try {
    const currentParams = {
      ...buildBaseParams(),
      channel: behaviorFilters.channel || '',
      indexName: behaviorFilters.keyword || ''
    }
    const compareParams = {
      ...buildBaseParams(getCompareRange(dateRange.value)),
      channel: behaviorFilters.channel || '',
      indexName: behaviorFilters.keyword || ''
    }

    const tasks = [fetchBehaviorList(currentParams)]
    if (compareEnabled.value) {
      tasks.push(fetchBehaviorList(compareParams))
    }
    const [list, compareList] = await Promise.all(tasks)
    if (context.requestId && (context.requestId !== dashboardRequestSeq || currentProject.value?.id !== context.projectId)) {
      return
    }
    behaviorCurrent.value = Array.isArray(list) ? list : []
    behaviorCompare.value = compareEnabled.value && Array.isArray(compareList) ? compareList : []

    if (!selectedBehaviorName.value && behaviorCurrent.value.length > 0) {
      await showBehaviorDetail(behaviorCurrent.value[0])
    } else if (selectedBehaviorName.value) {
      const found = behaviorCurrent.value.find((item) => item.indexName === selectedBehaviorName.value)
      if (found) {
        await showBehaviorDetail(found)
      } else {
        resetBehaviorDetail()
      }
    }
  } catch (error) {
    console.error(error)
    ElMessage.error(error.message || '加载行为数据失败')
  } finally {
    if (!context.requestId || context.requestId === dashboardRequestSeq) {
      behaviorLoading.value = false
    }
  }
}

async function refreshBehavior() {
  await loadBehaviorData()
}

function handleBehaviorKeywordSearch(query) {
  clearBehaviorSearchTimer()
  behaviorSearchLoading.value = true
  behaviorSearchTimer = window.setTimeout(async () => {
    behaviorFilters.keyword = query || ''
    try {
      await loadBehaviorData()
    } finally {
      behaviorSearchLoading.value = false
    }
  }, 280)
}

async function handleBehaviorSelectVisibleChange(visible) {
  if (!visible) return
  if (behaviorRows.value.length) return
  behaviorSearchLoading.value = true
  try {
    behaviorFilters.keyword = ''
    await loadBehaviorData()
  } finally {
    behaviorSearchLoading.value = false
  }
}

async function handleBehaviorSelectChange(value) {
  if (!value) return
  const found = behaviorRows.value.find((item) => item.indexName === value)
  if (found) {
    await showBehaviorDetail(found)
    return
  }
  behaviorFilters.keyword = value
  behaviorSearchLoading.value = true
  try {
    await loadBehaviorData()
    const matched = behaviorRows.value.find((item) => item.indexName === value)
    if (matched) {
      await showBehaviorDetail(matched)
    }
  } finally {
    behaviorSearchLoading.value = false
  }
}

async function handleBehaviorSelectClear() {
  behaviorFilters.keyword = ''
  behaviorSearchLoading.value = true
  try {
    await loadBehaviorData()
  } finally {
    behaviorSearchLoading.value = false
  }
}

async function showBehaviorDetail(row) {
  if (!row?.indexName) return
  selectedBehaviorName.value = row.indexName
  selectedBehaviorSummary.value = behaviorRows.value.find((item) => item.indexName === row.indexName) || null

  try {
    behaviorDetailLoading.value = true
    const currentParams = {
      ...buildBaseParams(),
      indexName: row.indexName,
      channel: behaviorFilters.channel || ''
    }
    const compareParams = {
      ...buildBaseParams(getCompareRange(dateRange.value)),
      indexName: row.indexName,
      channel: behaviorFilters.channel || ''
    }
    const tasks = [fetchBehaviorDetail(currentParams)]
    if (compareEnabled.value) tasks.push(fetchBehaviorDetail(compareParams))
    const [detail, compareDetail] = await Promise.all(tasks)
    behaviorDetail.value = normalizeBehaviorDetail(detail)
    behaviorCompareDetail.value = compareEnabled.value ? normalizeBehaviorDetail(compareDetail) : createEmptyBehaviorDetail()
  } catch (error) {
    console.error(error)
    ElMessage.error(error.message || '加载行为明细失败')
  } finally {
    behaviorDetailLoading.value = false
  }
}

function handleBehaviorRowClick(row) {
  showBehaviorDetail(row)
}

function handleBehaviorSelectionChange(rows) {
  behaviorSelection.value = rows.map((item) => item.indexName)
}

function resetBehaviorDetail() {
  selectedBehaviorName.value = ''
  selectedBehaviorSummary.value = null
  behaviorDetail.value = createEmptyBehaviorDetail()
  behaviorCompareDetail.value = createEmptyBehaviorDetail()
}

function buildBaseParams(range = dateRange.value) {
  return {
    projectId: currentProject.value?.id,
    startTime: range?.[0],
    endTime: range?.[1]
  }
}

function openExportDrawer() {
  if (!currentProject.value) {
    ElMessage.warning('请先选择项目')
    return
  }
  exportDateRange.value = Array.isArray(dateRange.value) ? [...dateRange.value] : getDefaultRange()
  if (!exportSheets.value.length) {
    resetExportSheetDraft(activeTab.value === 'behavior' ? 'behavior' : 'traffic')
  }
  exportDrawerVisible.value = true
}

function handleExportDraftCategoryChange(category) {
  resetExportSheetDraft(category)
}

function selectDraftChannels() {
  exportSheetDraft.channels = ['__overall__', ...channelOptions.value.map((item) => item.value)]
}

function clearDraftChannels() {
  exportSheetDraft.channels = []
}

function selectAllDraftBehaviors() {
  exportSheetDraft.behaviors = Array.from(new Set([...exportSheetDraft.behaviors, ...exportBehaviorOptions.value.map((item) => item.value)]))
}

function clearDraftBehaviors() {
  exportSheetDraft.behaviors = []
}

function removeDraftBehavior(name) {
  exportSheetDraft.behaviors = exportSheetDraft.behaviors.filter((item) => item !== name)
}

function removeDraftChannel(channel) {
  exportSheetDraft.channels = exportSheetDraft.channels.filter((item) => item !== channel)
}

function resetExportSheetDraft(category = 'traffic') {
  exportSheetDraft.category = category
  exportSheetDraft.sheetName = category === 'behavior' ? `行为数据_${exportSheets.value.filter((item) => item.category === 'behavior').length + 1}` : `流量数据_${exportSheets.value.filter((item) => item.category === 'traffic').length + 1}`
  exportSheetDraft.channels = ['__overall__']
  exportSheetDraft.behaviorKeyword = ''
  exportSheetDraft.behaviors = []
  exportBehaviorPool.value = []
}

function appendExportSheet() {
  const sheetName = (exportSheetDraft.sheetName || '').trim()
  if (!sheetName) {
    ElMessage.warning('请填写 sheet 名称')
    return
  }
  if (!exportSheetDraft.channels.length) {
    ElMessage.warning('请至少选择一个渠道对象')
    return
  }
  if (exportSheetDraft.category === 'behavior' && !exportSheetDraft.behaviors.length) {
    ElMessage.warning('请至少勾选一个行为')
    return
  }
  exportSheets.value.push({
    id: `sheet_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    category: exportSheetDraft.category,
    sheetName,
    channels: [...exportSheetDraft.channels],
    behaviors: exportSheetDraft.category === 'behavior' ? [...exportSheetDraft.behaviors] : []
  })
  resetExportSheetDraft(exportSheetDraft.category)
}

function removeExportSheet(id) {
  exportSheets.value = exportSheets.value.filter((item) => item.id !== id)
}

function formatExportTargets(targets = []) {
  return targets.map((item) => (item === '__overall__' ? '整体' : item)).join('、')
}

function formatBehaviorPreview(behaviors = []) {
  if (!behaviors.length) return '-'
  if (behaviors.length <= 3) return behaviors.join('、')
  return `${behaviors.slice(0, 3).join('、')} 等 ${behaviors.length} 项`
}

function handleExportBehaviorKeywordSearch(query) {
  clearExportBehaviorSearchTimer()
  exportBehaviorSearchLoading.value = true
  exportBehaviorSearchTimer = window.setTimeout(async () => {
    exportSheetDraft.behaviorKeyword = query || ''
    try {
      await loadExportBehaviorOptions(query || '')
    } finally {
      exportBehaviorSearchLoading.value = false
    }
  }, 280)
}

async function runExportBehaviorSearch() {
  clearExportBehaviorSearchTimer()
  exportBehaviorSearchLoading.value = true
  try {
    await loadExportBehaviorOptions(exportSheetDraft.behaviorKeyword || '')
  } finally {
    exportBehaviorSearchLoading.value = false
  }
}

async function handleExportBehaviorKeywordClear() {
  clearExportBehaviorSearchTimer()
  exportSheetDraft.behaviorKeyword = ''
  exportBehaviorPool.value = []
}

async function loadExportBehaviorOptions(keyword = '') {
  if (!currentProject.value?.id) {
    exportBehaviorPool.value = []
    return
  }
  const result = await fetchBehaviorList({
    ...buildBaseParams(exportDateRange.value),
    channel: '',
    indexName: keyword
  })
  exportBehaviorPool.value = Array.isArray(result) ? result : []
}

async function handleExport() {
  if (!currentProject.value) {
    ElMessage.warning('请先选择项目')
    return
  }
  if (!exportSheets.value.length) {
    ElMessage.warning('请先加入至少一个导出 sheet')
    return
  }
  lastExportAction.value = 'mixed'
  exporting.value = true
  startExportProgress()
  try {
    await exportConfiguredWorkbook()
    exportDrawerVisible.value = false
    finishExportProgress('success', '导出完成')
    ElMessage.success('导出成功')
  } catch (error) {
    console.error(error)
    finishExportProgress('exception', error.message || '导出失败')
    ElMessage.error(error.message || '导出失败')
  } finally {
    exporting.value = false
  }
}

async function exportConfiguredWorkbook() {
  const sheets = []
  const metrics = getMetricList(exportForm.metricMode)
  const dates = buildDateList(exportDateRange.value[0], exportDateRange.value[1])
  const totalSteps = exportSheets.value.reduce((sum, sheet) => {
    return sum + (sheet.category === 'traffic' ? sheet.channels.length : sheet.channels.length * sheet.behaviors.length)
  }, 0)
  initExportProgress(totalSteps, `准备导出 ${exportSheets.value.length} 个 sheet`)

  for (const sheetConfig of exportSheets.value) {
    if (sheetConfig.category === 'traffic') {
      sheets.push(await buildTrafficExportSheet(sheetConfig, dates, metrics))
    } else {
      sheets.push(await buildBehaviorExportSheet(sheetConfig, dates, metrics))
    }
  }

  exportWorkbook({
    sheets,
    fileName: buildExportFileName({
      projectName: currentProject.value.name,
      projectCode: currentProject.value.code,
      dataLabel: exportSheets.value.length > 1 ? '组合数据' : exportSheets.value[0]?.category === 'behavior' ? '行为数据' : '流量数据',
      startDate: exportDateRange.value[0],
      endDate: exportDateRange.value[1]
    })
  })
}

async function buildTrafficExportSheet(sheetConfig, dates, metrics) {
  const targets = sortTrafficTargets(sheetConfig.channels.length ? [...sheetConfig.channels] : ['__overall__'])
  const valueMap = {}
  for (const target of targets) {
    const label = target === '__overall__' ? '整体' : target
    stepExportProgress(`正在拉取流量数据：${sheetConfig.sheetName} / ${label}`, false, `${sheetConfig.sheetName} / ${label}`)
    const params = buildBaseParams(exportDateRange.value)
    if (target !== '__overall__') {
      params.channel = target
    }
    const result = normalizeTraffic(await fetchBasicMain(params))
    valueMap[label] = {}
    dates.forEach((date) => {
      valueMap[label][date] = { pv: 0, uv: 0 }
    })
      ; (result.dailyBasics || []).forEach((item) => {
        valueMap[label][item.date] = {
          pv: toNumber(item.pv),
          uv: toNumber(item.uv)
        }
      })
    stepExportProgress(`已完成流量数据：${sheetConfig.sheetName} / ${label}`, true, `${sheetConfig.sheetName} / ${label}`)
  }
  return buildPivotSheet({
    sheetName: sheetConfig.sheetName,
    orientation: exportForm.orientation,
    dateList: dates,
    metrics,
    items: targets.map((item) => (item === '__overall__' ? '整体' : item)),
    valueMap,
    themeKey: exportForm.themeKey
  })
}

async function buildBehaviorExportSheet(sheetConfig, dates, metrics) {
  const targets = sortTrafficTargets(sheetConfig.channels.length ? [...sheetConfig.channels] : ['__overall__'])
  const sortedBehaviors = sortBehaviorNames(sheetConfig.behaviors || [])
  const items = []
  const valueMap = {}
  for (const target of targets) {
    const channelLabel = target === '__overall__' ? '整体' : target
    for (const behaviorName of sortedBehaviors) {
      const itemLabel = targets.length > 1 ? `${channelLabel} / ${behaviorName}` : behaviorName
      items.push(itemLabel)
      valueMap[itemLabel] = {}
      dates.forEach((date) => {
        valueMap[itemLabel][date] = { pv: 0, uv: 0 }
      })
      stepExportProgress(`正在处理行为数据：${sheetConfig.sheetName} / ${itemLabel}`, false, `${sheetConfig.sheetName} / ${itemLabel}`)
      const params = {
        ...buildBaseParams(exportDateRange.value),
        indexName: behaviorName,
        channel: target === '__overall__' ? '' : target
      }
      const result = normalizeBehaviorDetail(await fetchBehaviorDetail(params))
        ; (result.dailyCustoms || []).forEach((item) => {
          valueMap[itemLabel][item.date] = {
            pv: toNumber(item.pv),
            uv: toNumber(item.uv)
          }
        })
      stepExportProgress(`已完成行为数据：${sheetConfig.sheetName} / ${itemLabel}`, true, `${sheetConfig.sheetName} / ${itemLabel}`)
    }
  }
  return buildPivotSheet({
    sheetName: sheetConfig.sheetName,
    orientation: exportForm.orientation,
    dateList: dates,
    metrics,
    items,
    valueMap,
    themeKey: exportForm.themeKey
  })
}

function ensureExportChannelDefaults() {
  if (!Array.isArray(exportSheetDraft.channels) || !exportSheetDraft.channels.length) {
    exportSheetDraft.channels = ['__overall__']
  }
}

function sortBehaviorNames(list = []) {
  return [...list].sort((a, b) => String(a || '').localeCompare(String(b || ''), 'en'))
}

function sortTrafficTargets(list = []) {
  return [...list].sort((a, b) => {
    if (a === '__overall__') return -1
    if (b === '__overall__') return 1
    return String(a || '').localeCompare(String(b || ''), 'en')
  })
}

function resetExportDrawerState() {
  exportSheets.value = []
  exportBehaviorPool.value = []
  exportDateRange.value = Array.isArray(dateRange.value) ? [...dateRange.value] : getDefaultRange()
  resetExportSheetDraft(activeTab.value === 'behavior' ? 'behavior' : 'traffic')
}

function backToProjectList() {
  viewMode.value = 'list'
  nextTick(() => {
    document.querySelector('.project-browser')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

function openProjectExport(project) {
  projectExportProject.value = project
  projectExportVisible.value = true
}

function normalizeVersion(version) {
  const text = String(version || 'v4').toLowerCase()
  return text === 'v3' ? 'V3' : 'V4'
}

function setTrackingCheckProgress(percentage, message) {
  trackingCheckProgress.percentage = percentage
  trackingCheckProgress.message = message
}

async function openTrackingCheck(project) {
  if (!project?.code) return
  trackingCheckProject.value = project
  trackingCheckVisible.value = true
  trackingCheckLoadingId.value = project.id
  trackingCheckRows.value = []
  trackingCheckSummary.value = []
  trackingOtherRows.value = []
  trackingSuggestions.value = []
  await runTrackingCheck(project)
}

async function rerunTrackingCheck() {
  if (!trackingCheckProject.value) return
  await runTrackingCheck(trackingCheckProject.value)
}

async function runTrackingCheck(project) {
  trackingChecking.value = true
  trackingCheckLoadingId.value = project.id
  try {
    const checkDate = new Date()
    trackingCheckDate.value = formatDate(checkDate)
    setTrackingCheckProgress(10, '生成检测 SQL')
    const sql = buildTrackingCheckSql(project.code, checkDate)
    setTrackingCheckProgress(35, '正在查询今日埋点数据')
    const result = await api.executeDataWorksSql(sql)
    if (result?.success === false) {
      throw new Error(result?.errorMsg || result?.message || '埋点检测查询失败')
    }
    setTrackingCheckProgress(70, '正在分析埋点结果')
    const rows = normalizeTrackingRows(result?.data)
    trackingCheckRows.value = rows
    const analysis = analyzeTrackingRows(rows, normalizeVersion(project.version).toLowerCase())
    trackingCheckSummary.value = analysis.summary
    trackingOtherRows.value = analysis.others
    trackingSuggestions.value = analysis.suggestions
    setTrackingCheckProgress(100, rows.length ? '检查完成' : '今日暂无埋点数据')
  } catch (error) {
    console.error(error)
    trackingSuggestions.value = ['检测请求失败，请确认 SQL 权限、项目 code 或今日数据分区是否正常。']
    setTrackingCheckProgress(100, error.message || '检测失败')
    ElMessage.error(error.message || '检测埋点失败')
  } finally {
    trackingChecking.value = false
    if (trackingCheckLoadingId.value === project.id) {
      trackingCheckLoadingId.value = null
    }
  }
}

function buildTrackingCheckSql(projectCode, checkDate) {
  const year = String(checkDate.getFullYear())
  const month = String(checkDate.getMonth() + 1).padStart(2, '0')
  const day = String(checkDate.getDate()).padStart(2, '0')
  const startTime = `${year}-${month}-${day} 00:00:00`
  const nextDate = new Date(checkDate)
  nextDate.setDate(nextDate.getDate() + 1)
  const nextDay = formatDate(nextDate)
  return `
SELECT
  type,
  customtype,
  page,
  module,
  COUNT(userid) pv,
  COUNT(DISTINCT userid) uv,
  SUM(value) AS value
FROM mf_kafka_tracking
WHERE projectcode IN ('${String(projectCode).replace(/'/g, "\\'")}')
  AND timestamp >= TO_MILLIS(DATETIME '${startTime}')
  AND timestamp < TO_MILLIS(DATETIME '${nextDay} 00:00:00')
  AND year = '${year}'
  AND month = '${month}'
  AND day = '${day}'
GROUP BY type, customtype, page, module
;
`.trim()
}

function normalizeTrackingRows(data) {
  const rows = Array.isArray(data) ? data : []
  return rows.map((item) => ({
    type: item.type || '',
    customtype: item.customtype || '',
    page: item.page || '',
    module: item.module || '',
    pv: toNumber(item.pv),
    uv: toNumber(item.uv),
    value: toNumber(item.value)
  }))
}

function analyzeTrackingRows(rows, version) {
  const matchedRowSet = new Set()
  const summary = TRACKING_CHECK_RULES.map((rule) => {
    const matched = rows.filter((row) => rule.match(version, row))
    matched.forEach((row) => matchedRowSet.add(row))
    const pv = matched.reduce((sum, row) => sum + toNumber(row.pv), 0)
    const uv = matched.reduce((sum, row) => sum + toNumber(row.uv), 0)
    const value = matched.reduce((sum, row) => sum + toNumber(row.value), 0)
    const isHealthy = rule.hasValue ? value > 0 : pv > 0 || uv > 0
    return {
      key: rule.key,
      label: rule.label,
      pv,
      uv,
      value,
      hasValue: rule.hasValue,
      statusText: isHealthy ? '有数据' : '待确认',
      statusType: isHealthy ? 'success' : 'warning',
      tip: buildTrackingTip(rule.key, { pv, uv, value }, version)
    }
  })

  const others = rows.filter((row) => !matchedRowSet.has(row))
  const suggestions = buildTrackingSuggestions(summary, others, version, rows.length)
  return { summary, others, suggestions }
}

function buildTrackingTip(key, values, version) {
  if (key === 'traffic') {
    return version === 'v3'
      ? 'V3 流量主要看 type = flowSource。'
      : 'V4 流量主要看 type = visit。'
  }
  if (key === 'click') {
    return version === 'v3'
      ? 'V3 点击主要看 customtype 非空。'
      : 'V4 点击主要看 type = click。'
  }
  if (key === 'stayTime') {
    return values.value > 0 ? '已检测到停留时长累计值，可继续看均值是否合理。' : '未检测到有效停留时长 value。'
  }
  return '可继续扩展更多规则。'
}

function buildTrackingSuggestions(summary, others, version, totalRows) {
  const suggestions = []
  if (!totalRows) {
    suggestions.push('今天当前项目还没有查到聚合埋点数据，先确认今日是否真的有访问与埋点上报。')
  }
  const traffic = summary.find((item) => item.key === 'traffic')
  const click = summary.find((item) => item.key === 'click')
  const stayTime = summary.find((item) => item.key === 'stayTime')
  if (traffic && traffic.pv === 0 && traffic.uv === 0) {
    suggestions.push(`流量埋点为空，${version === 'v3' ? '重点检查 type=flowSource' : '重点检查 type=visit'} 是否正常上报。`)
  }
  if (click && click.pv === 0 && click.uv === 0) {
    suggestions.push(`点击埋点为空，${version === 'v3' ? '可以先看 customtype 字段是否缺失' : '可以先看 type=click 是否接入'}。`)
  }
  if (stayTime && stayTime.value === 0) {
    suggestions.push('停留时长没有有效 value，建议确认 timeOnSite 的计算结果和上报时机。')
  }
  if (others.length) {
    suggestions.push(`发现 ${others.length} 条未归类埋点类型，后续可以把常用类型补进检测规则。`)
  }
  if (!suggestions.length) {
    suggestions.push('核心埋点项都有数据，接下来可以结合原始聚合结果继续排查 page、module 和 customtype 分布。')
  }
  return suggestions
}

function renderCharts() {
  if (activeTab.value === 'traffic') {
    renderTrafficTrendChart()
    renderTrafficChannelChart()
    return
  }

  if (activeTab.value === 'behavior') {
    renderBehaviorTrendChart()
    renderBehaviorChannelChart()
  }
}

function renderTrafficTrendChart() {
  if (!trafficTrendChartRef.value) return
  if (!trafficTrendChart) trafficTrendChart = echarts.init(trafficTrendChartRef.value)
  const daily = trafficCurrent.value?.dailyBasics || []
  const metric = trafficTrendMetric.value
  const compareDaily = trafficCompare.value?.dailyBasics || []

  trafficTrendChart.setOption(
    {
      color: getThemePalette(activeTheme.value),
      tooltip: { trigger: 'axis' },
      legend: { top: 0 },
      grid: { left: 40, right: 24, top: 48, bottom: 30 },
      xAxis: { type: 'category', data: daily.map((item) => item.date) },
      yAxis: { type: 'value' },
      series: [
        {
          name: `当前${metric.toUpperCase()}`,
          type: 'line',
          smooth: true,
          data: daily.map((item) => toNumber(item[metric])),
          areaStyle: {}
        },
        compareEnabled.value
          ? {
            name: `对比${metric.toUpperCase()}`,
            type: 'line',
            smooth: true,
            lineStyle: { type: 'dashed' },
            data: compareDaily.map((item) => toNumber(item[metric]))
          }
          : null
      ].filter(Boolean)
    },
    true
  )
}

function renderTrafficChannelChart() {
  if (!trafficChannelChartRef.value) return
  if (!trafficChannelChart) trafficChannelChart = echarts.init(trafficChannelChartRef.value)
  const metric = trafficChannelMetric.value
  const rows = trafficChannelRows.value.slice(0, 8)

  trafficChannelChart.setOption(
    {
      color: getThemePalette(activeTheme.value),
      tooltip: { trigger: 'item' },
      legend: { bottom: 0, type: 'scroll' },
      series: [
        {
          name: metric.toUpperCase(),
          type: 'pie',
          radius: ['40%', '68%'],
          center: ['50%', '46%'],
          label: {
            formatter: '{b}\n{d}%'
          },
          data: rows.map((item) => ({
            name: item.channel,
            value: toNumber(item[metric])
          }))
        }
      ]
    },
    true
  )
}

function renderBehaviorTrendChart() {
  if (!behaviorTrendChartRef.value) return
  if (!behaviorTrendChart) behaviorTrendChart = echarts.init(behaviorTrendChartRef.value)
  const daily = behaviorDetail.value?.dailyCustoms || []
  const compareDaily = behaviorCompareDetail.value?.dailyCustoms || []
  const metric = behaviorTrendMetric.value

  behaviorTrendChart.setOption(
    {
      color: getThemePalette(activeTheme.value),
      tooltip: { trigger: 'axis' },
      legend: { top: 0 },
      grid: { left: 40, right: 24, top: 48, bottom: 30 },
      xAxis: { type: 'category', data: daily.map((item) => item.date) },
      yAxis: { type: 'value' },
      series: [
        {
          name: `当前${metric.toUpperCase()}`,
          type: 'bar',
          barMaxWidth: 24,
          data: daily.map((item) => toNumber(item[metric]))
        },
        compareEnabled.value
          ? {
            name: `对比${metric.toUpperCase()}`,
            type: 'line',
            smooth: true,
            data: compareDaily.map((item) => toNumber(item[metric]))
          }
          : null
      ].filter(Boolean)
    },
    true
  )
}

function renderBehaviorChannelChart() {
  if (!behaviorChannelChartRef.value) return
  if (!behaviorChannelChart) behaviorChannelChart = echarts.init(behaviorChannelChartRef.value)
  const rows = behaviorDetail.value?.channelRateList || []

  behaviorChannelChart.setOption(
    {
      color: getThemePalette(activeTheme.value),
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 40, right: 24, top: 20, bottom: 60 },
      xAxis: {
        type: 'category',
        axisLabel: { rotate: 22 },
        data: rows.map((item) => item.channel)
      },
      yAxis: { type: 'value' },
      series: [
        {
          name: 'PV',
          type: 'bar',
          data: rows.map((item) => toNumber(item.pv))
        },
        {
          name: 'UV',
          type: 'bar',
          data: rows.map((item) => toNumber(item.uv))
        }
      ]
    },
    true
  )
}

function resizeCharts() {
  trafficTrendChart?.resize()
  trafficChannelChart?.resize()
  behaviorTrendChart?.resize()
  behaviorChannelChart?.resize()
}

function disposeCharts() {
  trafficTrendChart?.dispose()
  trafficChannelChart?.dispose()
  behaviorTrendChart?.dispose()
  behaviorChannelChart?.dispose()
  trafficTrendChart = null
  trafficChannelChart = null
  behaviorTrendChart = null
  behaviorChannelChart = null
}

function normalizeTraffic(data) {
  return {
    pv: toNumber(data?.pv),
    uv: toNumber(data?.uv),
    clickUser: toNumber(data?.clickUser),
    clickCount: toNumber(data?.clickCount),
    clickRate: toNumber(data?.clickRate),
    dropRate: toNumber(data?.dropRate),
    avgStayTime: data?.avgStayTime || '0',
    sevenDayReturnUser: toNumber(data?.sevenDayReturnUser),
    dailyBasics: Array.isArray(data?.dailyBasics) ? data.dailyBasics : [],
    channelRateList: Array.isArray(data?.channelRateList) ? data.channelRateList : []
  }
}

function normalizeBehaviorDetail(data) {
  return {
    pv: toNumber(data?.pv),
    uv: toNumber(data?.uv),
    dailyCustoms: Array.isArray(data?.dailyCustoms) ? data.dailyCustoms : [],
    channelRateList: Array.isArray(data?.channelRateList) ? data.channelRateList : []
  }
}

function createEmptyTraffic() {
  return {
    pv: 0,
    uv: 0,
    clickUser: 0,
    clickCount: 0,
    clickRate: 0,
    dropRate: 0,
    avgStayTime: '0',
    sevenDayReturnUser: 0,
    dailyBasics: [],
    channelRateList: []
  }
}

function createEmptyBehaviorDetail() {
  return {
    pv: 0,
    uv: 0,
    dailyCustoms: [],
    channelRateList: []
  }
}

function buildKpi(key, label, currentValue, compareValue, sub, tagType, isPercent = false) {
  const delta = toNumber(currentValue) - toNumber(compareValue)
  const percent = calcMom(currentValue, compareValue)
  return {
    key,
    label,
    value: isPercent ? formatPercent(currentValue) : formatNumber(currentValue),
    delta,
    percent,
    sub,
    tag: compareEnabled.value ? '对比中' : '当前值',
    tagType
  }
}

function getMetricList(mode) {
  if (mode === 'pv') return ['pv']
  if (mode === 'uv') return ['uv']
  return ['pv', 'uv']
}

function getDefaultRange() {
  const end = getRelativeDate(-1)
  const start = new Date()
  start.setTime(end.getTime())
  start.setDate(start.getDate() - 6)
  return [formatDate(start), formatDate(end)]
}

function getPresetRange(preset) {
  if (preset === 'today') {
    const today = getRelativeDate(0)
    return [formatDate(today), formatDate(today)]
  }

  const end = getRelativeDate(-1)
  const start = new Date(end)
  const sizeMap = {
    yesterday: 1,
    last3: 3,
    last7: 7,
    last30: 30,
    last90: 90
  }
  const days = sizeMap[preset] || 7
  start.setDate(start.getDate() - days + 1)
  return [formatDate(start), formatDate(end)]
}

function getCompareRange(range) {
  const start = new Date(range[0])
  const end = new Date(range[1])
  const diffDays = Math.round((end.getTime() - start.getTime()) / 86400000) + 1
  const compareEnd = new Date(start)
  compareEnd.setDate(compareEnd.getDate() - 1)
  const compareStart = new Date(compareEnd)
  compareStart.setDate(compareStart.getDate() - diffDays + 1)
  return [formatDate(compareStart), formatDate(compareEnd)]
}

function buildDateList(startDate, endDate) {
  const list = []
  const cursor = new Date(startDate)
  const end = new Date(endDate)
  while (cursor <= end) {
    list.push(formatDate(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return list
}

function formatDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatNow() {
  const now = new Date()
  const date = formatDate(now)
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  return `${date} ${hours}:${minutes}:${seconds}`
}

function getRelativeDate(offsetDays) {
  const date = new Date()
  date.setDate(date.getDate() + offsetDays)
  return date
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

function calcRate(value, total) {
  const numerator = toNumber(value)
  const denominator = toNumber(total)
  if (!denominator) return 0
  return numerator / denominator
}

function calcMom(current, previous) {
  const currentValue = toNumber(current)
  const previousValue = toNumber(previous)
  if (!previousValue) {
    if (!currentValue) return 0
    return 1
  }
  return (currentValue - previousValue) / previousValue
}

function formatNumber(value) {
  const num = toNumber(value)
  return new Intl.NumberFormat('zh-CN').format(num)
}

function formatPercent(value) {
  return `${(toNumber(value) * 100).toFixed(2)}%`
}

function formatStayTime(value) {
  const seconds = toNumber(value)
  if (!seconds) return '0s'
  if (seconds < 60) return `${seconds.toFixed(0)}s`
  return `${(seconds / 60).toFixed(2)}m`
}

function formatDeltaText(delta, percent) {
  const deltaValue = toNumber(delta)
  const percentText = formatPercent(percent)
  if (deltaValue === 0) return `持平 ${percentText}`
  return `${deltaValue > 0 ? '+' : ''}${formatNumber(deltaValue)} / ${percentText}`
}

function formatMom(value) {
  const num = toNumber(value)
  if (num === 0) return '环比 0.00%'
  return `环比 ${num > 0 ? '+' : ''}${(num * 100).toFixed(2)}%`
}

function deltaClass(value) {
  const num = toNumber(value)
  if (num > 0) return 'is-up'
  if (num < 0) return 'is-down'
  return 'is-flat'
}

function startExportProgress() {
  clearExportAutoCloseTimer()
  exportProgressVisible.value = true
  exportProgress.status = ''
  exportProgress.current = 0
  exportProgress.percentage = 0
  exportProgress.message = '准备导出...'
  exportProgress.currentLabel = ''
  exportProgress.remaining = 0
}

function initExportProgress(total, message) {
  exportProgress.total = Math.max(total, 1)
  exportProgress.current = 0
  exportProgress.percentage = 0
  exportProgress.message = message
  exportProgress.status = ''
  exportProgress.currentLabel = ''
  exportProgress.remaining = exportProgress.total
}

function stepExportProgress(message, increase = false, currentLabel = '') {
  if (increase) {
    exportProgress.current += 1
  }
  exportProgress.message = message
  if (currentLabel) exportProgress.currentLabel = currentLabel
  exportProgress.remaining = Math.max(exportProgress.total - exportProgress.current, 0)
  exportProgress.percentage = Math.min(
    100,
    Math.round((exportProgress.current / Math.max(exportProgress.total, 1)) * 100)
  )
}

function finishExportProgress(status, message) {
  exportProgress.current = exportProgress.total
  exportProgress.percentage = 100
  exportProgress.status = status
  exportProgress.message = message
  exportProgress.remaining = 0
  if (status === 'success') {
    exportAutoCloseTimer = window.setTimeout(() => {
      exportProgressVisible.value = false
    }, 1600)
  }
}

function closeExportProgressDialog() {
  clearExportAutoCloseTimer()
  exportProgressVisible.value = false
}

function clearExportAutoCloseTimer() {
  if (exportAutoCloseTimer) {
    window.clearTimeout(exportAutoCloseTimer)
    exportAutoCloseTimer = null
  }
}

function clearBehaviorSearchTimer() {
  if (behaviorSearchTimer) {
    window.clearTimeout(behaviorSearchTimer)
    behaviorSearchTimer = null
  }
}

function clearExportBehaviorSearchTimer() {
  if (exportBehaviorSearchTimer) {
    window.clearTimeout(exportBehaviorSearchTimer)
    exportBehaviorSearchTimer = null
  }
}

async function retryLastExport() {
  closeExportProgressDialog()
  if (!lastExportAction.value) return
  await handleExport()
}

function getThemePalette(theme) {
  const palettes = {
    aurora: ['#2563eb', '#14b8a6', '#f97316', '#8b5cf6', '#ef4444', '#0ea5e9'],
    slate: ['#334155', '#0f766e', '#475569', '#0284c7', '#7c3aed', '#dc2626'],
    graphite: ['#111827', '#4b5563', '#1d4ed8', '#0f766e', '#ca8a04', '#7c2d12']
  }
  return palettes[theme] || palettes.aurora
}
</script>

<style scoped>
.report-data-board {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: #f5f7fb;
  min-height: calc(100vh - 120px);
}

.hero-band,
.panel-shell,
.placeholder-shell,
.kpi-card,
.detail-card {
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.hero-band {
  padding: 24px;
}

.hero-main {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
}

.hero-copy h1 {
  margin: 0;
  font-size: 30px;
  color: #111827;
}

.hero-copy p {
  margin: 8px 0 0;
  color: #64748b;
  line-height: 1.6;
}

.hero-meta {
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: #64748b;
  font-size: 13px;
}

.project-browser {
  padding: 18px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.sticky-action-bar {
  position: sticky;
  top: 12px;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #dbe2ea;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.07);
  backdrop-filter: blur(10px);
}

.sticky-project {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.sticky-inline-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.sticky-filter-chip {
  min-height: 34px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  align-items: center;
  gap: 8px;
}

.sticky-filter-chip span {
  color: #475569;
  font-size: 12px;
  font-weight: 600;
}

.sticky-filter-chip small {
  color: #64748b;
  font-size: 12px;
}

.theme-chip {
  padding-right: 6px;
}

.sticky-date {
  flex: 0 0 auto;
}

.sticky-project strong {
  color: #0f172a;
  font-size: 14px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sticky-project span {
  color: #64748b;
  font-size: 12px;
}

.sticky-meta-pill {
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  line-height: 1;
}

.sticky-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.sticky-actions :deep(.el-button) {
  min-height: 34px;
  min-width: 64px;
}

.sticky-date :deep(.el-date-editor.el-input__wrapper),
.sticky-date :deep(.el-date-editor) {
  min-height: 34px;
}

.sticky-filter-chip :deep(.el-select__wrapper) {
  min-height: 28px;
}

.project-toolbar {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.project-card-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.project-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.project-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px rgba(37, 99, 235, 0.08);
  border-color: #bfdbfe;
}

.project-card.is-current {
  border-color: #60a5fa;
  box-shadow: 0 0 0 1px rgba(96, 165, 250, 0.28);
}

.project-card.is-switching {
  border-color: #f59e0b;
  background: linear-gradient(180deg, #fffdf7 0%, #fff7ed 100%);
}

.project-card-head,
.project-card-actions,
.project-card-tags {
  display: flex;
}

.project-card-head {
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.project-card-title h4 {
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  line-height: 1.4;
}

.project-card-title p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
}

.project-card-tags {
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.project-card-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.project-metric-item {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.project-metric-item span,
.project-card-extra span {
  color: #64748b;
  font-size: 12px;
}

.project-metric-item strong,
.project-card-extra strong {
  color: #0f172a;
}

.project-card-extra {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.project-card-actions {
  gap: 8px;
  flex-wrap: wrap;
}

.project-card-empty {
  padding: 20px 0 4px;
}

.project-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 16px;
}

.kpi-card {
  padding: 16px;
}

.kpi-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  color: #475569;
  font-size: 13px;
}

.kpi-value {
  margin: 14px 0 8px;
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
}

.kpi-compare {
  margin: 0;
  font-size: 13px;
}

.kpi-sub {
  margin: 8px 0 0;
  font-size: 12px;
  color: #94a3b8;
}

.board-tabs :deep(.el-tabs__header) {
  margin-bottom: 16px;
}

.panel-grid {
  display: grid;
  gap: 16px;
}

.two-up {
  grid-template-columns: minmax(0, 1.4fr) minmax(360px, 1fr);
}

.split-grid {
  grid-template-columns: minmax(0, 1.5fr) minmax(320px, 0.9fr);
  margin-top: 16px;
}

.panel-shell {
  padding: 18px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.panel-header h3 {
  margin: 0;
  font-size: 18px;
  color: #0f172a;
}

.panel-header p {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}

.chart-box {
  width: 100%;
}

.large-chart {
  height: 360px;
}

.medium-chart {
  height: 360px;
}

.export-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.summary-item {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.summary-item span {
  font-size: 12px;
  color: #64748b;
}

.summary-item strong {
  color: #0f172a;
}

.table-panel {
  margin-top: 16px;
}

.metric-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.metric-cell small {
  font-size: 12px;
}

.filter-strip {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.behavior-detail-band {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin: 16px 0;
}

.detail-card {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-card span {
  font-size: 12px;
  color: #64748b;
}

.detail-card strong {
  color: #0f172a;
}

.placeholder-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}

.placeholder-shell {
  padding: 32px;
}

.placeholder-shell h3 {
  margin: 0 0 10px;
  color: #0f172a;
}

.placeholder-shell p,
.placeholder-shell li {
  color: #64748b;
  line-height: 1.7;
}

.export-drawer {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.export-sheet-builder,
.export-sheet-list {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 16px;
  margin-bottom: 16px;
}

.draft-sheet-actions {
  margin-top: 4px;
}

.sheet-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sheet-config-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
}

.sheet-config-head,
.sheet-config-meta,
.export-search-row,
.sheet-selected-preview {
  display: flex;
}

.sheet-config-head {
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.sheet-config-head strong {
  color: #0f172a;
}

.sheet-config-head p,
.sheet-config-text,
.sheet-config-meta span,
.export-empty-tip {
  color: #64748b;
  font-size: 12px;
}

.sheet-config-head p,
.sheet-config-text {
  margin: 4px 0 0;
}

.sheet-config-meta {
  gap: 12px;
  margin: 10px 0 0;
}

.export-search-row {
  width: 100%;
  gap: 10px;
  align-items: center;
}

.export-search-row :deep(.el-input) {
  flex: 1;
}

.behavior-export-grid {
  max-height: 220px;
  overflow: auto;
  padding-right: 6px;
}

.sheet-selected-preview {
  width: 100%;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 32px;
  padding: 8px 0 2px;
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
}

.export-preview {
  margin-top: 8px;
  padding: 14px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.export-inline-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 8px;
}

.export-progress-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.export-progress-title {
  color: #0f172a;
  font-weight: 600;
}

.export-progress-meta {
  display: flex;
  justify-content: space-between;
  color: #64748b;
  font-size: 13px;
}

.export-progress-extra {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.5;
}

.empty-project-shell {
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
  padding: 32px 16px;
}

.tracking-check-shell {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.tracking-check-head,
.tracking-check-actions,
.tracking-summary-top,
.tracking-summary-values {
  display: flex;
}

.tracking-check-head {
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.tracking-check-head strong {
  color: #0f172a;
  font-size: 16px;
}

.tracking-check-head p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
}

.tracking-progress-panel,
.tracking-suggestion-shell,
.tracking-other-shell,
.tracking-summary-card {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.tracking-progress-panel,
.tracking-suggestion-shell,
.tracking-other-shell {
  padding: 16px;
}

.tracking-progress-title {
  margin-bottom: 12px;
  color: #0f172a;
  font-weight: 600;
}

.tracking-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.tracking-summary-card {
  padding: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.tracking-summary-top {
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.tracking-summary-values {
  gap: 12px;
  flex-wrap: wrap;
  margin: 14px 0 10px;
  color: #0f172a;
  font-weight: 600;
}

.tracking-summary-card p,
.tracking-suggestion-shell li {
  color: #64748b;
  line-height: 1.7;
}

.tracking-suggestion-shell h4 {
  margin: 0 0 10px;
  color: #0f172a;
}

.tracking-suggestion-shell ul {
  margin: 0;
  padding-left: 18px;
}

.export-preview strong {
  color: #0f172a;
}

.export-preview p {
  margin: 8px 0 0;
  color: #64748b;
  line-height: 1.6;
}

.drawer-footer {
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
}

.table-actions {
  display: flex;
  align-items: center;
}

.is-up {
  color: #059669;
}

.is-down {
  color: #dc2626;
}

.is-flat {
  color: #64748b;
}

.board-fade-enter-active,
.board-fade-leave-active {
  transition: all 0.28s ease;
}

.board-fade-enter-from,
.board-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (max-width: 1280px) {
  .kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .behavior-detail-band,
  .tracking-summary-grid,
  .project-card-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .two-up,
  .split-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 768px) {
  .report-data-board {
    padding: 16px;
  }

  .hero-main {
    flex-direction: column;
  }

  .filter-strip,
  .project-toolbar {
    width: 100%;
  }

  .project-card-grid,
  .project-card-metrics {
    grid-template-columns: minmax(0, 1fr);
  }

  .sticky-action-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .sticky-project,
  .sticky-inline-filters {
    width: 100%;
  }

  .sticky-date :deep(.el-date-editor) {
    width: 100% !important;
  }

  .sticky-actions {
    width: 100%;
  }

  .sticky-actions :deep(.el-button) {
    flex: 1;
  }

  .filter-strip :deep(.el-button) {
    flex: 1;
  }

  .kpi-grid,
  .behavior-detail-band,
  .tracking-summary-grid,
  .export-summary,
  .checkbox-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
